import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { PERMISSION_WORD, PERMISSION_TONE } from '@/lib/smsWarnings.js';
import { recordSmsPermission, stopSmsPermission } from '@/services/sms-permission.service.js';

// The text permission handle (people-create-door T7e, DA3): the store's answer as
// a word beside its colour, and David's two doors, each asking a reason in a
// dialog. "Record permission" is what David heard (it also lifts a STOP the
// number texted); "Stop texting" reads as no permission. The sheet re-reads the
// store after either (onChanged), so the badge is a read, not an echo.
const ACTIONS = {
  grant: {
    label: 'Record permission',
    title: 'Record text permission',
    describe: (state) => (state === 'stopped_by_number'
      ? 'This number texted STOP. Recording permission lifts it. Say what the client told you and when.'
      : 'Record that the client agreed to texts at this number. Say how you heard it.'),
    send: recordSmsPermission,
    done: 'Text permission recorded',
  },
  revoke: {
    label: 'Stop texting',
    title: 'Stop texting this number',
    describe: () => 'Record that the client asked not to be texted at this number. Say how you heard it.',
    send: stopSmsPermission,
    done: 'Texting stopped for this number',
  },
};

export default function SmsPermissionControl({ phone, state, at, crmContactId, onChanged }) {
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!state) return null;
  const a = action ? ACTIONS[action] : null;
  const offer = state === 'granted' ? 'revoke' : (state === 'none' || state === 'stopped_by_number') ? 'grant' : null;

  function open(kind) { setReason(''); setAction(kind); }

  async function submit() {
    setBusy(true);
    try {
      const r = await a.send({ phone, reason: reason.trim(), crmContactId });
      const warnings = Array.isArray(r?.warnings) ? r.warnings : [];
      if (!r?.unchanged) toast.success(a.done);
      for (const w of warnings) toast.info(w.message || w.code);
      setAction(null);
      onChanged?.();
    } catch (err) {
      toast.error((err instanceof ApiError && err.body?.error) || err.message || 'Could not record that');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${PERMISSION_TONE[state] || PERMISSION_TONE.unreadable}`}>
        {PERMISSION_WORD[state] || PERMISSION_WORD.unreadable}{state === 'granted' && at ? ` (${String(at).slice(0, 10)})` : ''}
      </span>
      {offer && phone ? (
        <Button type="button" variant="outline" size="xs" onClick={() => open(offer)}>{ACTIONS[offer].label}</Button>
      ) : null}
      <Dialog open={!!a} onOpenChange={(o) => { if (!o && !busy) setAction(null); }}>
        {a ? (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{a.title}</DialogTitle>
              <DialogDescription>{a.describe(state)}</DialogDescription>
            </DialogHeader>
            <div>
              <label className="text-xs font-medium text-text-muted" htmlFor="sms-permission-reason">Reason (required)</label>
              <textarea
                id="sms-permission-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
              />
              <p className="mt-1 text-[11px] text-text-dim">{phone}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAction(null)} disabled={busy}>Cancel</Button>
              <Button type="button" onClick={submit} disabled={busy || !reason.trim()}>{busy ? 'Saving…' : a.label}</Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
}
